import React, { useEffect } from 'react';
import { NameySays } from './Mascot';

export default function DictionarySheet({ word, result, loading, error, onClose }) {
  // Close on backdrop tap or Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!word) return null;

  const wikiUrl = `https://en.wikipedia.org/wiki/${encodeURIComponent(word)}`;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-50"
        onClick={onClose}
      />

      {/* Sheet */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl shadow-2xl max-h-[70vh] overflow-y-auto">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-gray-200" />
        </div>

        <div className="px-5 pb-8 pt-2">
          {/* Word + close */}
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-2xl font-black text-gray-800 capitalize">{word}</h2>
              {result?.phonetic && (
                <p className="text-sm text-gray-400 mt-0.5">{result.phonetic}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-xl font-bold p-1 transition-colors"
            >
              ✕
            </button>
          </div>

          {loading && (
            <NameySays mood="thinking" className="py-2">
              <p className="text-gray-500 text-sm">Hmm, let me look that up…</p>
            </NameySays>
          )}

          {error && (
            <NameySays mood="sad" className="py-2">
              <p className="text-gray-500 text-sm mb-3">I couldn't reach the dictionary right now.</p>
              <a
                href={wikiUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-600 hover:text-red-700"
              >
                Search Wikipedia →
              </a>
            </NameySays>
          )}

          {result && !loading && (
            result.notFound ? (
              /* Proper noun / not in dictionary */
              <NameySays mood="dizzy">
                <p className="text-sm text-gray-500 mb-3">
                  I can't find this one in the dictionary. It could be a proper noun, a place, or a word from another language.
                </p>
                <a
                  href={wikiUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-600 hover:text-red-700"
                >
                  Look up on Wikipedia →
                </a>
              </NameySays>
            ) : (
              /* Dictionary entry */
              <div className="space-y-4">
                <NameySays mood="happy">
                  <div className="space-y-4">
                    {result.meanings.map((m, i) => (
                      <div key={i}>
                        {m.partOfSpeech && (
                          <span className="text-xs font-bold text-red-500 uppercase tracking-wide bg-red-50 px-2 py-0.5 rounded-full">
                            {m.partOfSpeech}
                          </span>
                        )}
                        <p className="text-gray-700 text-sm mt-2 leading-relaxed">{m.definition}</p>
                        {m.example && (
                          <p className="text-gray-400 text-xs mt-1 italic">"{m.example}"</p>
                        )}
                      </div>
                    ))}
                  </div>
                </NameySays>

                {result.categoryMatch?.matches && (
                  <p className="text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">
                    ✓ Looks like it fits the category
                  </p>
                )}
                {result.source && result.source !== 'dictionary' && (
                  <p className="text-xs text-gray-400">
                    Not in Merriam-Webster · definition from {result.source === 'ai' ? 'AI' : 'Wikipedia'}
                  </p>
                )}

                {/* Always offer Wikipedia link as well */}
                <a
                  href={wikiUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-red-500 transition-colors"
                >
                  More on Wikipedia →
                </a>
              </div>
            )
          )}
        </div>
      </div>
    </>
  );
}
